"use client"

import { Button, Text, Image } from "@/elements"
import React, { FC, useEffect, useState } from "react"
import { ConfettiExplosion } from "./ConfettiExplosion"
import Link from "next/link"
import { AvatarGroup } from "@/elements/AvatarGroup"
import { PostAJobResponseDataFragment } from "@/gql/graphql"
import { JobTitleColorised } from "../JobTitleColorised"
import { useFormContext } from "react-hook-form"
import { PostAJobModalFormValues } from "./types"

export type CreativeInvite = {
  avatarUrl: string
  avatarInitials: string
}

interface Props {
  wrapperRef: React.RefObject<HTMLDivElement | null>
  jobPostingResponseData: PostAJobResponseDataFragment | null
  invitedCreatives: CreativeInvite[]
}

export const SuccessStep: FC<Props> = ({ wrapperRef, jobPostingResponseData, invitedCreatives }) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const { watch } = useFormContext<PostAJobModalFormValues>()
  const referenceImage = watch("referenceImage")
  const referenceImageUrl = referenceImage.file || referenceImage?.existingImageUrl

  useEffect(() => {
    const updateDimensions = () => {
      if (wrapperRef.current) {
        const { width, height } = wrapperRef.current.getBoundingClientRect()
        setDimensions({ width, height }) // Halve the width and height
      }
    }

    // Initialize dimensions
    updateDimensions()
  }, [wrapperRef])

  return (
    <div className="relative h-screen px-3 pb-10 pt-[15vh] sm:h-auto sm:px-[70px] sm:pt-[88px]">
      <div
        className="pointer-events-none fixed bottom-0 left-0 right-0 top-0 z-50"
        style={{
          height: dimensions.height,
          width: dimensions.width,
        }}>
        <ConfettiExplosion maxXDistance={dimensions.width} maxYDistance={dimensions.height} />
      </div>
      <div className="flex h-full flex-col items-center">
        <div className="flex h-full w-full flex-col items-center justify-center">
          <Text variant="h5" className="text-center">
            Congratulations!
          </Text>
          <Text variant="h5" className="text-center">
            Your job is live.
          </Text>
          <div className="mt-10 w-full overflow-hidden rounded-lg border border-bg-3 bg-bg-2">
            {referenceImageUrl ? (
              <div className="h-[100px] w-full overflow-hidden">
                <Image
                  className="h-full w-full object-cover"
                  srcSetUrls={{
                    sm: referenceImageUrl,
                  }}
                  alt="Cover image"
                />
              </div>
            ) : null}
            <div className="p-6">
              <JobTitleColorised textVariant="body1" commonWordColor="text-fg-2" jobPost={jobPostingResponseData} />
              <div className="mt-4 flex flex-row items-center justify-between">
                <Text className="text-fg-2" variant="body3">
                  just now
                </Text>
                <div className="flex flex-shrink-0 flex-row items-center gap-2 rounded-md bg-bg-3 px-2 py-2">
                  <Text variant="body2" className="text-fg-1">
                    {jobPostingResponseData?.payString}
                  </Text>
                </div>
              </div>
            </div>
          </div>
          <div className="flex w-full flex-1 flex-col-reverse sm:flex-col">
            <div className="mt-auto flex w-full flex-row items-center justify-center sm:mt-10">
              <Link href={`/hire/${jobPostingResponseData?.id}?tab=invites`} className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto">Manage job</Button>
              </Link>
            </div>
            {invitedCreatives.length > 0 && (
              <div className="mt-10 flex flex-row items-center justify-center gap-3">
                <AvatarGroup
                  avatarSize="sm"
                  items={invitedCreatives.slice(0, 3).map(invite => ({
                    initials: invite.avatarInitials,
                    src: invite.avatarUrl,
                  }))}
                />
                <Text variant="body2">You invited {invitedCreatives.length} creatives to this job!</Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
